import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { SEND_REQUEST_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiSend, FiArrowLeft, FiArrowRight } = FiIcons;

const TOTAL_STEPS = 4;

// Gemeinsame Input-Klassen — einmal definiert, überall genutzt.
const inputClass =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors';

// Animation-Varianten für bedingte Felder (sanftes Ein-/Ausblenden in der Höhe).
const conditionalField = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

// Schrittwechsel: neuer Schritt kommt aus der Richtung, in die navigiert wurde.
const stepVariants = {
  enter: (direction) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -40 : 40, opacity: 0 }),
};

const SectionHeading = ({ children }) => (
  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
    {children}
  </h3>
);

const RequestForm = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage].requestForm;
  const w = t.wizard;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    location: '',
    status: '',
    websiteLink: '',
    scope: [],
    scopeOther: '',
    goals: '',
    languages: [],
    languageOther: '',
    budget: '',
    timeline: '',
    support: [],
    notes: '',
    privacy: false
  });

  // Bot-Falle: ein für Menschen unsichtbares Feld. Ist es ausgefüllt, war es ein Bot.
  const [honeypot, setHoneypot] = useState('');

  // Zeitfalle: Wann wurde das Formular geöffnet? Ein Mensch braucht mindestens
  // ein paar Sekunden zum Ausfüllen, ein Bot postet sofort. Bewusst useRef statt
  // useState — der Wert steht so schon beim ersten Rendern fest.
  const formLoadedAt = useRef(Date.now());

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      if (name === 'privacy') {
        setFormData(prev => ({ ...prev, [name]: checked }));
      } else if (name === 'scope' || name === 'languages' || name === 'support') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
            ? [...prev[name], value]
            : prev[name].filter(item => item !== value),
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Fehler zu diesem Feld verschwindet, sobald der Nutzer es korrigiert.
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // Nur Schritt 1 hat Pflichtfelder (Name, E-Mail).
  const validateStep = (currentStep) => {
    const errors = {};
    if (currentStep === 1) {
      if (!formData.name.trim()) errors.name = w.errors.name;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = w.errors.email;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goToStep = (target) => {
    setDirection(target > step ? 1 : -1);
    setStep(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    goToStep(Math.min(step + 1, TOTAL_STEPS));
  };

  const handleBack = () => goToStep(Math.max(step - 1, 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enter-Taste in einem Eingabefeld darf nicht vorzeitig absenden.
    if (step < TOTAL_STEPS) {
      handleNext();
      return;
    }
    if (!validateStep(1)) {
      goToStep(1);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Die Benachrichtigungsmail soll immer deutsch sein — unabhängig davon, in
      // welcher Sprache das Formular ausgefüllt wurde. Deshalb bewusst die
      // deutschen Labels, nicht die der aktuellen Oberflächensprache.
      const de = translations.de.requestForm.options;
      const label = (key, group) => (key ? group[key] || key : '');
      const labelList = (keys, group) => keys.map((k) => group[k] || k).join(', ');

      const response = await fetch(SEND_REQUEST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          privacy: undefined,                    // Einwilligung ist Formularlogik, kein Inhalt
          website_hp: honeypot,                  // Bot-Falle: von Menschen immer leer
          form_loaded_at: formLoadedAt.current,  // Zeitfalle: zu schnell = Bot
          lang: currentLanguage,
          // Klartext für die Mail (die Rohwerte oben bleiben für die Datenbank).
          labels: {
            status: label(formData.status, de.status),
            scope: labelList(formData.scope, de.scope),
            languages: labelList(formData.languages, de.languages),
            budget: label(formData.budget, de.budget),
            timeline: label(formData.timeline, de.timeline),
            support: labelList(formData.support, de.support),
          },
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Nur wo ein echter Nutzer handeln kann, sagen wir warum. Abgewiesene
        // Bots (Honeypot, Zeitfalle) bekommen bewusst die generische Meldung,
        // damit sie nicht lernen, woran sie gescheitert sind.
        const { error: reason } = await response.json().catch(() => ({}));
        const messages = {
          rate_limited: t.errorRateLimited,
          disposable_email: t.errorDisposable,
          stale: t.errorStale,
        };
        setError(messages[reason] || t.errorSend);
      }
    } catch {
      setError(t.errorSend);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="bg-green-50 rounded-xl p-10 text-center border border-green-200 shadow-sm"
      >
        <motion.div
          initial={{ scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 220, damping: 14 }}
          className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6"
        >
          <SafeIcon icon={FiCheck} className="h-8 w-8 text-green-600" />
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="text-2xl font-bold text-gray-900 mb-4"
        >
          {t.success}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="text-gray-600"
        >
          Ihre Anfrage wurde erfolgreich versendet. Wir werden uns in Kürze bei Ihnen melden.
        </motion.p>
      </motion.div>
    );
  }

  // Auswahl-Schlüssel in lesbare Labels übersetzen (für die Zusammenfassung).
  const labelsFor = (keys, source) =>
    keys.map(k => source[k] || k).join(', ') || w.notSpecified;
  const valueOr = (v) => (v && String(v).trim() ? v : w.notSpecified);

  const summaryRows = [
    [t.fields.name, formData.name],
    [t.fields.email, formData.email],
    [t.fields.phone, valueOr(formData.phone)],
    [t.fields.company, valueOr(formData.company)],
    [t.fields.industry, valueOr(formData.industry)],
    [t.fields.location, valueOr(formData.location)],
    [t.sections.website, formData.status ? t.options.status[formData.status] : w.notSpecified],
    [t.fields.websiteLink, valueOr(formData.websiteLink)],
    [t.sections.scope, labelsFor(formData.scope, t.options.scope)],
    [t.fields.otherScope, valueOr(formData.scopeOther)],
    [t.options.languages.other, labelsFor(formData.languages, t.options.languages)],
    [t.fields.goals, valueOr(formData.goals)],
    [t.options.budget.label, formData.budget ? t.options.budget[formData.budget] : w.notSpecified],
    [t.options.timeline.label, formData.timeline ? t.options.timeline[formData.timeline] : w.notSpecified],
    [t.sections.cooperation, labelsFor(formData.support, t.options.support)],
    [t.fields.notes, valueOr(formData.notes)],
  ];

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">

      {/* Fortschrittsanzeige */}
      <div className="px-6 md:px-10 pt-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-blue-700">
            {w.step} {step} {w.of} {TOTAL_STEPS}
          </span>
          <span className="text-sm text-gray-500">{w.stepNames[step]}</span>
        </div>
        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-blue-600"
            initial={false}
            animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </div>

      <div className="p-6 md:p-10">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-10"
          >

            {/* ---------- Schritt 1: Kontakt & Unternehmen ---------- */}
            {step === 1 && (
              <>
                <section>
                  <SectionHeading>{t.sections.contact}</SectionHeading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.name} *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`${inputClass} ${fieldErrors.name ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                      />
                      {fieldErrors.name && <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.email} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`${inputClass} ${fieldErrors.email ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                      />
                      {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.phone}</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.company}</label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange} className={inputClass} />
                    </div>
                  </div>
                </section>

                <section>
                  <SectionHeading>{t.sections.company}</SectionHeading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.industry}</label>
                      <input type="text" name="industry" value={formData.industry} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.location}</label>
                      <input type="text" name="location" value={formData.location} onChange={handleChange} className={inputClass} />
                    </div>
                  </div>
                </section>
              </>
            )}

            {/* ---------- Schritt 2: Website-Status & Umfang ---------- */}
            {step === 2 && (
              <>
                <section>
                  <SectionHeading>{t.sections.website}</SectionHeading>
                  <div className="space-y-4">
                    {Object.entries(t.options.status).map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="status"
                          value={key}
                          checked={formData.status === key}
                          onChange={handleChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="text-gray-700">{label}</span>
                      </label>
                    ))}
                    <AnimatePresence initial={false}>
                      {formData.status !== 'none' && formData.status !== '' && (
                        <motion.div
                          key="website-link"
                          variants={conditionalField}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.websiteLink}</label>
                            <input type="url" name="websiteLink" value={formData.websiteLink} onChange={handleChange} className={inputClass} />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </section>

                <section>
                  <SectionHeading>{t.sections.scope}</SectionHeading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {Object.entries(t.options.scope).map(([key, label]) => (
                      <label key={key} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="scope"
                          value={key}
                          checked={formData.scope.includes(key)}
                          onChange={handleChange}
                          className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                        />
                        <span className="text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>

                  <AnimatePresence initial={false}>
                    {formData.scope.includes('other') && (
                      <motion.div
                        key="scope-other"
                        variants={conditionalField}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden mb-6"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.otherScope}</label>
                          <input type="text" name="scopeOther" value={formData.scopeOther} onChange={handleChange} className={inputClass} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">{t.options.languages.other}</h4>
                    <div className="flex flex-wrap gap-4">
                      {['de', 'en', 'sv', 'hu'].map((lang) => (
                        <label key={lang} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="languages"
                            value={lang}
                            checked={formData.languages.includes(lang)}
                            onChange={handleChange}
                            className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-gray-700">{t.options.languages[lang]}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.goals}</label>
                    <textarea name="goals" rows="3" value={formData.goals} onChange={handleChange} className={inputClass}></textarea>
                  </div>
                </section>
              </>
            )}

            {/* ---------- Schritt 3: Budget, Zeitrahmen & Zusammenarbeit ---------- */}
            {step === 3 && (
              <>
                <section>
                  <SectionHeading>{t.sections.budget}</SectionHeading>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3">{t.options.budget.label}</h4>
                      <div className="space-y-3">
                        {['unknown', 'small', 'medium', 'large'].map((key) => (
                          <label key={key} className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="budget"
                              value={key}
                              checked={formData.budget === key}
                              onChange={handleChange}
                              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-gray-700 text-sm">{t.options.budget[key]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3">{t.options.timeline.label}</h4>
                      <div className="space-y-3">
                        {['asap', 'months', 'later'].map((key) => (
                          <label key={key} className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="timeline"
                              value={key}
                              checked={formData.timeline === key}
                              onChange={handleChange}
                              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-gray-700 text-sm">{t.options.timeline[key]}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <SectionHeading>{t.sections.cooperation}</SectionHeading>
                  <div className="space-y-3 mb-6">
                    {Object.entries(t.options.support).map(([key, label]) => (
                      <label key={key} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="support"
                          value={key}
                          checked={formData.support.includes(key)}
                          onChange={handleChange}
                          className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                        />
                        <span className="text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.fields.notes}</label>
                    <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className={inputClass}></textarea>
                  </div>
                </section>
              </>
            )}

            {/* ---------- Schritt 4: Zusammenfassung, Datenschutz & Absenden ---------- */}
            {step === 4 && (
              <>
                <section>
                  <SectionHeading>{w.review}</SectionHeading>
                  <p className="text-sm text-gray-600 mb-6">{w.reviewHint}</p>
                  <dl className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                    {summaryRows.map(([label, value]) => (
                      <div key={label} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 px-4 py-3 odd:bg-gray-50">
                        <dt className="text-sm font-medium text-gray-700">{label}</dt>
                        <dd className="sm:col-span-2 text-sm text-gray-900 break-words whitespace-pre-wrap">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className="bg-gray-50 p-6 rounded-lg">
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        key="error-box"
                        initial={{ opacity: 0, height: 0, y: -8 }}
                        animate={{ opacity: 1, height: 'auto', y: 0 }}
                        exit={{ opacity: 0, height: 0, y: -8 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden mb-6"
                      >
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <p className="text-sm text-gray-600 mb-4">{t.privacy.text}</p>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleChange}
                      className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-900">{t.privacy.checkbox} *</span>
                  </label>
                </section>
              </>
            )}

          </motion.div>
        </AnimatePresence>

        {/* Bot-Falle: für Menschen unsichtbar, Bots füllen es aus. */}
        <input
          type="text"
          name="website_hp"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
        />

        {/* ---------- Navigation ---------- */}
        <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between gap-4">
          {step > 1 ? (
            <motion.button
              type="button"
              onClick={handleBack}
              whileHover={{ x: -3 }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2 h-5 w-5" />
              {w.back}
            </motion.button>
          ) : (
            <span />
          )}

          {step < TOTAL_STEPS ? (
            <motion.button
              type="button"
              onClick={handleNext}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-3 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-colors duration-200 flex items-center"
            >
              {w.next}
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </motion.button>
          ) : (
            <motion.button
              type="submit"
              disabled={!formData.privacy || loading}
              whileHover={!formData.privacy || loading ? {} : { y: -2, scale: 1.02 }}
              whileTap={!formData.privacy || loading ? {} : { scale: 0.98 }}
              className={`px-8 py-4 rounded-lg font-bold text-white shadow-lg transition-colors duration-200 flex items-center justify-center ${
                !formData.privacy || loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl'
              }`}
            >
              {loading ? (
                <>
                  <motion.span
                    className="inline-block"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  >
                    <SafeIcon icon={FiSend} className="h-5 w-5" />
                  </motion.span>
                  <span className="ml-2">Wird versendet...</span>
                </>
              ) : (
                <>
                  {t.submit}
                  <SafeIcon icon={FiSend} className="ml-2 h-5 w-5" />
                </>
              )}
            </motion.button>
          )}
        </div>
      </div>
    </form>
  );
};

export default RequestForm;
